import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'


import { MenuEntity, MenuItemEntity, UseMenuParams } from '../model/types'

const MENU_QUERY = gql`
  query Menu($code: String!) {
    menu(code: $code) {
      id
      code
      name
      type
      is_active
      menuItems {
        id
        title
        link_type
        link_url
        icon_code
        icon_color
        text_color
        bold
        sort_order
        image {
          url
        }
      }
    }
  }
`

type MenuQueryType = {
  menu: {
    id: string
    code: string
    name: string
    type: string
    is_active: boolean
    menuItems: {
      id: string
      title: string
      link_type?: string
      link_url?: string
      icon_code?: string
      icon_color?: string
      text_color?: string
      bold?: boolean
      sort_order?: number
      image: { url?: string }
    }[]
  }
}

export function useMenu ({
  code,
  fetchPolicy = 'cache-and-network'
}: UseMenuParams) {
  const { data, loading, error, refetch } = useQuery<MenuQueryType>(MENU_QUERY, {
    variables: { code },
    fetchPolicy,
    skip: !code,
  })

  const menu: MenuEntity | null = useMemo(() => {
    if (!data?.menu) return null

    return {
      ...data.menu,
      menuItems: (data.menu.menuItems || [])
        .filter(Boolean)
        .map((item): MenuItemEntity => ({
          id: item!.id!,
          title: item!.title!,
          link_type: item!.link_type || undefined,
          link_url: item!.link_url || undefined,
          icon_code: item!.icon_code || undefined,
          icon_color: item!.icon_color || undefined,
          text_color: item!.text_color || undefined,
          bold: item!.bold || false,
          sort_order: item!.sort_order || 0,
          icon_url: item!.image?.url || undefined,
        }))
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    }
  }, [ data ])

  return {
    menu,
    data,
    loading,
    error,
    refetch,
  }
}
